import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ListItemText, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import classNames from 'classnames/bind';
import styles from 'client/modules/Surveys/components/SearchDateRange/SearchDateRange.css';
import FormatDateCalendar, { formatDateCalendar } from 'client/util/FormatDateCalendar';
import isEqual from 'lodash/isEqual';
import moment from 'moment/moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCalendar } from 'react-icons/fa';
import { defineMessages, useIntl } from 'react-intl';
import useDidUpdateEffect from '../../../../../server/util/useDidUpdateEffect';

export const messages = defineMessages({
  'Last 30 days': {
    id: 'SearchDateRange.Last30Days',
    defaultMessage: 'Last 30 days',
  },
  all: {
    id: 'SearchDateRange.AllTime',
    defaultMessage: 'All time',
  },
  'This week': {
    id: 'SearchDateRange.ThisWeek',
    defaultMessage: 'This week',
  },
  'Last week': {
    id: 'SearchDateRange.LastWeek',
    defaultMessage: 'Last week',
  },
  'This month': {
    id: 'SearchDateRange.ThisMonth',
    defaultMessage: 'This month',
  },
  'Last month': {
    id: 'SearchDateRange.LastMonth',
    defaultMessage: 'Last month',
  },
  'This quarter': {
    id: 'SearchDateRange.ThisQuarter',
    defaultMessage: 'This quarter',
  },
  'Last quarter': {
    id: 'SearchDateRange.LastQuarter',
    defaultMessage: 'Last quarter',
  },
  'This year': {
    id: 'SearchDateRange.ThisYear',
    defaultMessage: 'This year',
  },
  'Last year': {
    id: 'SearchDateRange.LastYear',
    defaultMessage: 'Last year',
  },
  'Custom range': {
    id: 'SearchDateRange.CustomRange',
    defaultMessage: 'Custom range...',
  },
  ok: {
    id: 'SearchDateRange.OK',
    defaultMessage: 'OK',
  },
  cancel: {
    id: 'SearchDateRange.Cancel',
    defaultMessage: 'Cancel',
  },
});

export const presets = [
  ['Last 30 days', 'now-30d/d', 'now'],
  ['This week', 'now/w', 'now'],
  ['Last week', 'now/w-1w/w', 'now/w-1w'],
  ['This month', 'now/M', 'now'],
  ['Last month', 'now/M-1M/M', 'now/M-1M'],
  ['This quarter', 'now/1q', 'now'],
  ['Last quarter', 'now/q-1q/q', 'now/q-1q'],
  ['This year', 'now/y', 'now'],
  ['Last year', 'now/y-1y/y', 'now/y-1y'],
];

export const datePresets = () => ({
  now: moment(),
  'now-30d/d': moment().startOf('day').subtract(30, 'days'),
  'now/w': moment().startOf('isoWeek'),
  'now/w-1w/w': moment().subtract(1, 'week').startOf('isoWeek'),
  'now/w-1w': moment().subtract(1, 'week').endOf('isoWeek'),
  'now/M': moment().startOf('month'),
  'now/M-1M/M': moment().subtract(1, 'month').startOf('month'),
  'now/M-1M': moment().subtract(1, 'month').endOf('month'),
  'now/1q': moment().startOf('quarter'),
  'now/q-1q/q': moment().subtract(1, 'quarter').startOf('quarter'),
  'now/q-1q': moment().subtract(1, 'quarter').endOf('quarter'),
  'now/y': moment().startOf('year'),
  'now/y-1y/y': moment().subtract(1, 'year').startOf('year'),
  'now/y-1y': moment().subtract(1, 'year').endOf('year'),
});

function PresetFilterComponent(props) {
  const { value } = props;
  const { formatMessage } = useIntl();
  const renderValue = useCallback(
    selected => {
      if (selected.length === 0) {
        return props.emptyValue;
      }
      const [start, end] = selected.split(':');
      const text = presets.find(p => p[1] === start && p[2] === end)[0];
      return formatMessage(messages[text]);
    },
    [formatMessage, props.emptyValue],
  );

  return (
    <Select
      renderValue={renderValue}
      variant="standard"
      disableUnderline
      value={value ? value.join(':') : null}
      onChange={event => props.onSetRange(...event.target.value.split(':'))}
      className={`${styles.formInput} ${styles.time}`}
      IconComponent={({ className }) =>
        value?.length > 0 ? (
          <IconButton
            className={className}
            style={{ pointerEvents: 'all', top: 'calc(50% - 20px)' }}
            onClick={() => {
              if (value?.length > 0) props.onSetRange();
            }}
          >
            {value?.length > 0 ? <ClearIcon /> : <ExpandMoreIcon />}
          </IconButton>
        ) : (
          <ExpandMoreIcon className={className} />
        )
      }
      displayEmpty
    >
      {presets.map(([text, start, end]) => (
        <MenuItem key={text} value={`${start}:${end}`} label="hioewjr">
          <ListItemText>{formatMessage(messages[text])}</ListItemText>
          {!start && end === 'now' ? null : (
            <Typography variant="body2" color="text.secondary">
              <FormatDateCalendar date={datePresets()[start]} />
              {' - '}
              <FormatDateCalendar date={datePresets()[end]} />
            </Typography>
          )}
        </MenuItem>
      ))}
      <MenuItem value=":">{formatMessage(messages['Custom range'])}</MenuItem>
    </Select>
  );
}

PresetFilterComponent.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSetRange: PropTypes.func.isRequired,
  emptyValue: PropTypes.string.isRequired,
};

const getStatus = (value, alwaysOpen) => {
  if (value?.length === 2) {
    const [start, end] = value;
    const preset = presets.find(p => p[1] === start && p[2] === end);
    if (preset?.[0] === 'Last 30 days') {
      return 'closed';
    }
    return preset ? 'presets' : 'custom';
  }
  return alwaysOpen ? 'presets' : 'closed';
};

function SearchDateRangeControl({ className, value: valueProp, onChange, emptyValue, alwaysOpen }) {
  const [value, setValue] = useState(valueProp || []);
  const [status, setStatus] = useState(getStatus(valueProp, alwaysOpen));
  const intl = useIntl();
  const prevValuePropRef = useRef(valueProp);
  const prevAlwaysOpenRef = useRef(alwaysOpen);

  useDidUpdateEffect(() => {
    if (!isEqual(value, valueProp)) {
      onChange(value, status);
    }
  }, [status, value]);

  const showPresets = useCallback(() => {
    setStatus('presets');
  }, []);

  const onSetRange = useCallback(
    (min, max) => {
      if ((min === '' && max !== 'now') || max === '') {
        setStatus('custom');
      } else if (typeof min === 'undefined' && typeof max === 'undefined') {
        if (!alwaysOpen) {
          setStatus('closed');
        }
        setValue([]);
      } else {
        setValue([min, max]);
      }
    },
    [alwaysOpen],
  );

  useEffect(() => {
    const valuePropChanged = !isEqual(prevValuePropRef.current, valueProp);
    const alwaysOpenChanged = prevAlwaysOpenRef.current !== alwaysOpen;

    if (valuePropChanged) {
      prevValuePropRef.current = valueProp;
      setValue(prev => (isEqual(prev, valueProp || []) ? prev : valueProp || []));
    }
    if (valuePropChanged || alwaysOpenChanged) {
      prevAlwaysOpenRef.current = alwaysOpen;
      setStatus(getStatus(valueProp, alwaysOpen));
    }
  }, [alwaysOpen, valueProp]);

  const [startDate, endDate] = value
    ? value.map(v => (v ? new Date(datePresets()[v] || v) : v))
    : [null, null];

  const searchTimeControls = {
    closed: (
      <button type="button" className={styles.formInput} onClick={showPresets}>
        <FaCalendar />
      </button>
    ),
    presets: (
      <PresetFilterComponent value={value} onSetRange={onSetRange} emptyValue={emptyValue} />
    ),
    custom: (
      <div className={styles.dates}>
        <DatePicker
          slotProps={{ textField: { sx: { mt: 0, minWidth: 0, width: 150 } } }}
          rifmFormatter={d => (d ? formatDateCalendar(intl, d) : null)}
          className={styles.dateInput}
          value={moment(startDate)}
          onChange={date => onSetRange(moment(date).format('YYYY-MM-DD'), value?.[1])}
        />
        <DatePicker
          slotProps={{ textField: { sx: { mt: 0, minWidth: 0, width: 150 } } }}
          rifmFormatter={d => (d ? formatDateCalendar(intl, d) : null)}
          className={styles.dateInput}
          value={moment(endDate)}
          onChange={date => onSetRange(value?.[0], moment(date).format('YYYY-MM-DD'))}
        />
        <IconButton
          aria-label="clear"
          onClick={() => {
            setValue([]);
            setStatus(getStatus([], alwaysOpen));
          }}
          sx={{
            width: 46,
            height: '100%',
            p: 0,
            flexShrink: 0,
            backgroundColor: '#fff',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderLeft: 0,
            borderRadius: '0 4px 4px 0',
            '&:hover': { backgroundColor: '#F7F8FA' },
          }}
        >
          <ClearIcon />
        </IconButton>
      </div>
    ),
  };

  return (
    <div className={classNames(className, styles.main, styles[status])}>
      {searchTimeControls[status]}
    </div>
  );
}

SearchDateRangeControl.propTypes = {
  className: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  emptyValue: PropTypes.string,
  alwaysOpen: PropTypes.bool,
};

SearchDateRangeControl.defaultProps = {
  className: null,
  value: [],
  onChange: () => {},
  emptyValue: '',
  alwaysOpen: false,
};

export default SearchDateRangeControl;
